import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab'
import { formatDateTime, formatMap } from '../utilities/formatDateTime'
import { useEffect, useEffectEvent, useMemo, useState } from 'react'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import fetchWithAuth from '../utilities/fetchWithAuth'
import { slugToLabel } from '../utilities/stringUtils'

const META_FIELDS = new Set(['author', 'createdAt', 'updatedAt', 'uuid'])
const PAGE_SIZE = 10

const HistoryTimeLine = ({
  endpoint,
  uuid,
  isActive,
  onSnapshotSelect,
  selectedSnapshotId,
  timezone,
  userDateFormat,
  refreshKey,
  disabled,
}) => {
  const dateFormat = formatMap[userDateFormat] || 'YYYY-MM-DD'

  const [history, setHistory] = useState(null)
  const [fetchError, setFetchError] = useState(false)
  const [filterDate, setFilterDate] = useState(null)
  const [historyPageCount, setHistoryPageCount] = useState(0)
  const [page, setPage] = useState(0)

  const fetchHistory = useEffectEvent(async () => {
    try {
      const params = new URLSearchParams({
        pageSize: PAGE_SIZE + 1,
        page,
        ...(filterDate?.from
          ? { from: dayjs(filterDate.from).format('YYYY-MM-DD') }
          : {}),
        ...(filterDate?.to
          ? { to: dayjs(filterDate.to).format('YYYY-MM-DD') }
          : {}),
      }).toString()
      const { data } = await fetchWithAuth(
        `${endpoint}/${uuid}/history?${params}`,
      )
      if (data && !data.error) {
        setHistory(data.data)
        setHistoryPageCount(Math.ceil(data.pagination.totalItems / PAGE_SIZE))
        setFetchError(false)
      } else {
        setHistory([])
        setFetchError(true)
      }
    } catch (error) {
      console.error('Error fetching history:', error)
      setHistory([])
      setFetchError(true)
    }
  })

  useEffect(() => {
    if (!isActive || !uuid) return
    fetchHistory()
  }, [filterDate, page, isActive, uuid, refreshKey])

  useEffect(() => {
    if (!isActive) {
      setHistory(null)
      setFetchError(false)
      setFilterDate(null)
      setPage(0)
    }
  }, [isActive])

  const enrichedHistory = useMemo(
    () =>
      (history ?? []).slice(0, PAGE_SIZE).map((snap, i) => {
        const older = history[i + 1]
        const keys = Object.keys(snap).filter((k) => !META_FIELDS.has(k))
        const changed = older
          ? keys.filter(
              (k) => JSON.stringify(snap[k]) !== JSON.stringify(older[k]),
            )
          : ['Created']
        return { snap, changed, older }
      }),
    [history],
  )

  const datePickerProps = {
    format: dateFormat,
    timezone: timezone,
    slotProps: { textField: { size: 'small', fullWidth: true } },
    sx: { mb: 2 },
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
      <DatePicker
        label="From"
        value={filterDate?.from ?? null}
        onChange={(newDate) => {
          setFilterDate((prev) => ({ ...prev, from: newDate }))
          setPage(0)
        }}
        {...datePickerProps}
      />
      <DatePicker
        label="Until"
        value={filterDate?.to ?? null}
        onChange={(newDate) => {
          setFilterDate((prev) => ({ ...prev, to: newDate }))
          setPage(0)
        }}
        {...datePickerProps}
      />
      {history === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {history !== null && fetchError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to load history.
        </Alert>
      )}
      {history !== null && !fetchError && enrichedHistory.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No history saved yet.
        </Alert>
      )}

      {enrichedHistory.length > 0 && (
        <>
          <Pagination
            count={historyPageCount}
            page={page + 1}
            onChange={(_, newPage) => setPage(newPage - 1)}
            size="small"
            sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
          />
          <Timeline sx={{ p: 0, mb: 6 }}>
            {enrichedHistory.map(({ snap, changed, older }, index) => (
              <TimelineItem key={snap.createdAt} sx={{ minHeight: 'auto' }}>
                <TimelineOppositeContent sx={{ maxWidth: '120px' }}>
                  <Button
                    variant="text"
                    disabled={disabled}
                    aria-pressed={selectedSnapshotId === snap.createdAt}
                    onClick={() => onSnapshotSelect?.(snap)}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    {formatDateTime(snap.createdAt, timezone, userDateFormat)}
                  </Button>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot
                    variant={
                      selectedSnapshotId === snap.createdAt
                        ? 'filled'
                        : 'outlined'
                    }
                  />
                  {index < enrichedHistory.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {snap.authorName ?? snap.author}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {changed.map((key) => {
                      const isCreated = key === 'Created'
                      const isNested =
                        !isCreated &&
                        snap[key] !== null &&
                        typeof snap[key] === 'object'
                      const noTooltip = isCreated
                      const oldValue = isNested
                        ? (older?.[key]?.name ?? older?.[key]?.uuid)
                        : older?.[key]
                      const newValue = isNested
                        ? (snap[key].name ?? snap[key].uuid)
                        : snap[key]
                      const tooltipTitle = [oldValue, newValue]
                        .filter(Boolean)
                        .join(' → ')
                      return (
                        <Tooltip
                          key={key}
                          title={tooltipTitle}
                          disableHoverListener={noTooltip}
                        >
                          <Chip
                            label={slugToLabel(
                              key.includes('.') ? key.split('.').pop() : key,
                            )}
                            size="small"
                            sx={{
                              maxWidth: 200,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              },
                            }}
                          />
                        </Tooltip>
                      )
                    })}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </>
      )}
    </LocalizationProvider>
  )
}

export default HistoryTimeLine
